import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

interface ProjectRouteProps {
  children: React.ReactNode;
}

export function ProjectRoute({ children }: ProjectRouteProps) {
  const { projectId } = useParams<{ projectId: string }>();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const validateProjectAccess = async () => {
      if (!projectId || projectId === 'skip') {
        // Invalid project ID or skip route - redirect to projects
        setIsValid(false);
        setIsValidating(false);
        return;
      }

      try {
        // Check if user has access to this project by trying to fetch project data
        // Use backups endpoint with minimal pagination as a lightweight way to validate access
        const response = await fetch(`/api/project/${projectId}/backups?per_page=1`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (response.ok) {
          setIsValid(true);
        } else {
          // Project doesn't exist or user doesn't have access
          setIsValid(false);
        }
      } catch (error) {
        // Network error or other issue - redirect to projects for safety
        setIsValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateProjectAccess();
  }, [projectId]);

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isValid) {
    // Invalid project ID or no access - redirect to projects
    return <Navigate to="/projects" replace />;
  }

  // Valid project - render children
  return <>{children}</>;
}